"use client";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CONTENT_SECTION_PRESETS, createContentSection, createSpecRow, useProductForm } from '../lib/useProductForm';
import { useAccessoryOptions } from '../lib/useAccessoryOptions';
import { useOfferImageSync } from '../lib/useOfferImageSync';
import { useAccessoryBindings, useCategoryOptions, useProductFormUiEffects } from '../lib/useProductFormUiEffects';
import CustomSelect from '../../../shared/ui/select-form/CustomSelect';
import CustomInput from '../../../shared/ui/input-form/CustomInput';
import CatalogPagination from '../../pagination/ui/Pagination'; 
import { uploadAdminImages, uploadAdminVideo, checkAdminCatalogOfferSku, applyAdminCatalogOfferImagesByColor } from '../../../shared/api/products.services';
import toast from '../../../shared/lib/toast';
import { colorPresetValueToHex } from '../../../shared/lib/colorPresetHex';
import { useAutoTranslate } from '../../../shared/lib/useAutoTranslate';
import TranslateButton from '../../../shared/ui/translate-button/TranslateButton';
import { BulkOffersModal, getAxisPresetLabel, getAxisPresetValue, sortAxisOptionsAsc } from '../../bulk-offers-modal';
import { getStatusOptions } from '../../../shared/lib/statuses';
import AdminPageLoader from '../../../shared/ui/admin-loader/AdminPageLoader';
import UnfilledAxesModal from './UnfilledAxesModal';

const STATUS_OPTIONS = getStatusOptions(['active', 'hidden', 'draft'], { labelType: 'form' });
const MAX_OFFER_IMAGES = 5;

// Видео в медиа-списке распознаём по расширению URL (как и галерея на сайте).
const isVideoMediaUrl = (url) => {
    const clean = String(url || '').split('?')[0].split('#')[0].toLowerCase();
    return /\.(mp4|webm|ogg|mov|m4v)$/.test(clean);
};
const isVideoFile = (file) => {
    if (!file) return false;
    if (String(file.type || '').toLowerCase().startsWith('video/')) return true;
    return isVideoMediaUrl(file.name);
};

// Відео автозапускається на сторінці товару, тож великі файли гальмують сайт.
// Обмежуємо розмір на завантаженні + показуємо пораду в інтерфейсі.
const MAX_VIDEO_SIZE_MB = 30;
const VIDEO_ADVICE = `Для стабільної роботи сайту: формат MP4 або WebM, до ${MAX_VIDEO_SIZE_MB} МБ, тривалість ~10–15 c, роздільність до 1080p. Великі відео сповільнюють відкриття товару.`;
// Повертає true, якщо файл валідний; інакше показує помилку і повертає false.
const isVideoFileWithinLimit = (file) => {
    if (!file) return false;
    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > MAX_VIDEO_SIZE_MB) {
        toast.error(`Відео завелике (${sizeMb.toFixed(1)} МБ). Максимум ${MAX_VIDEO_SIZE_MB} МБ — стисніть або скоротіть ролик.`);
        return false;
    }
    return true;
};
const MATERIAL_AXIS_ID = 'A7';
const COLOR_AXIS_ID = 'A1';

const DISCOUNT_TYPE_OPTIONS = [
    { value: 'none', label: 'Без знижки' },
    { value: 'percent', label: 'Відсоток (%)' },
    { value: 'amount', label: 'Сума (грн)' },
];

const ProductForm = ({
    type,
    initialData,
    variationsData,
    isBulkOffersModalOpen = false,
    onCloseBulkOffersModal = () => {},
}) => {
    const { formik, unfilledModal, resolveUnfilledModal } = useProductForm(type, initialData, variationsData);
    const { translateFields, isTranslating } = useAutoTranslate(formik);

    const isEditMode = type !== 'create';
    // Фільтр оферів — клієнтський (усі офери товару вже в формі). Жодних запитів
    // на сервер при зміні фільтра/сторінки → миттєво, без перезавантаження.
    const [localOfferFilters, setLocalOfferFilters] = useState({});
    const [isGroupVideoUploading, setIsGroupVideoUploading] = useState(false);
    const [isCardImageUploading, setIsCardImageUploading] = useState(false);
    const [cardImageDraft, setCardImageDraft] = useState('');
    const [bulkDiscountType, setBulkDiscountType] = useState('none');
    const [bulkDiscountValue, setBulkDiscountValue] = useState('');
    const [bulkMaterialValue, setBulkMaterialValue] = useState('');
    const [bulkPriceValue, setBulkPriceValue] = useState('');

    // === БАЗОВИЙ URL ДЛЯ МЕДІА ===
    const API_BASE_URL = (
        process.env.REACT_APP_API_URL_IMG?.trim() ||
        process.env.REACT_APP_API_URL?.trim() ||
        process.env.NEXT_PUBLIC_API_URL?.trim() ||
        ''
    );

    const normalizeBaseUrl = (baseUrl) => {
        const normalized = String(baseUrl || '').trim();
        if (!normalized) return '';
        if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
            return normalized;
        }
        if (normalized.startsWith('//')) {
            return `http:${normalized}`;
        }
        if (normalized.startsWith(':')) {
            return `http://localhost${normalized}`;
        }
        return `http://${normalized}`;
    };

    // Повертає абсолютний URL для /uploads і не ламається на некоректному base URL.
    const getFullImageUrl = (imgUrl) => {
        const rawUrl = String(imgUrl || '').trim();
        if (!rawUrl) return '';

        if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
            return rawUrl;
        }

        const normalizedBase = normalizeBaseUrl(API_BASE_URL);
        if (!normalizedBase) return rawUrl;

        try {
            return new URL(rawUrl, normalizedBase).toString();
        } catch {
            return rawUrl;
        }
    };

    // === СТЕЙТ ДЛЯ КАТЕГОРІЙ ===
    const { categoryOptions, categoryMetaById } = useCategoryOptions();
    const [accessorySearchQuery, setAccessorySearchQuery] = useState('');
    const { accessoryOptions: loadedAccessoryOptions, isAccessorySearchLoading } = useAccessoryOptions(
        accessorySearchQuery
    );

const axes = formik.values.variationAxes || [];
const offers = formik.values.offers || [];

useProductFormUiEffects({
    formik,
    categoryMetaById,
    initialData,
    variationsData,
    offers,
});

useOfferImageSync({
    axes,
    offers,
    setFieldValue: formik.setFieldValue,
});

const { accessoryOptions, applyAccessoryToAllOffers } = useAccessoryBindings({
    loadedAccessoryOptions,
    offers,
    setFieldValue: formik.setFieldValue,
});

const selectableAxes = useMemo(
    () => axes.filter((axis) => Array.isArray(axis.valuesPreset) && axis.valuesPreset.length > 0),
    [axes]
);

// Фільтр оферів — повністю клієнтський (усі офери товару завантажені в форму).
// Тож зміна фільтра/сторінки миттєва, без запиту на сервер і без перезавантаження.
const currentOpt = localOfferFilters;
const activeFilterCount = Object.values(currentOpt).filter((v) => String(v || '').trim() !== '').length;
const isAccessorySelectionDisabled = offers.length === 0;

const filteredOffers = useMemo(() => {
    const indexed = offers.map((item, originalIndex) => ({ item, originalIndex }));

    const activeFilters = Object.entries(currentOpt).filter(([, value]) => String(value || '').trim() !== '');
    if (!activeFilters.length) return indexed;

    return indexed.filter(({ item }) => activeFilters.every(([axisId, filterValue]) => {
        const offerValue = item?.options?.[axisId];
        return String(offerValue ?? '') === String(filterValue ?? '');
    }));
}, [offers, currentOpt]);

    // === КЛІЄНТСЬКА ПАГІНАЦІЯ ОФЕРІВ (рендеримо не всі картки одразу) ===
    const OFFERS_PER_PAGE = 24;
    const [offerPage, setOfferPage] = useState(1);
    const offerPageCount = Math.max(1, Math.ceil(filteredOffers.length / OFFERS_PER_PAGE));

    // Тримаємо поточну сторінку в межах доступної кількості (після фільтра/видалення).
    useEffect(() => {
        setOfferPage((prev) => Math.min(Math.max(1, prev), offerPageCount));
    }, [offerPageCount]);

    const pagedOffers = useMemo(() => {
        const start = (offerPage - 1) * OFFERS_PER_PAGE;
        return filteredOffers.slice(start, start + OFFERS_PER_PAGE);
    }, [filteredOffers, offerPage]);

    // === ЛОГІКА ОНОВЛЕННЯ ФІЛЬТРІВ ===
    const handleFilterChange = (axisId, value) => {
        const newOpt = { ...currentOpt };

        if (value) {
            newOpt[axisId] = value;
        } else {
            delete newOpt[axisId];
        }

        setLocalOfferFilters(newOpt);
        setOfferPage(1);
    };

    const handleResetFilters = () => {
        setLocalOfferFilters({});
        setOfferPage(1);
    };

    const handleAddOffer = () => {
        const newOffer = {
            title: '',
            price: '',
            sku: '',
            available: true,
            options: {},
            image: '',
            images: [],
            video: '',
            accessoryGroupId: String(formik.values.groupAccessoryGroupId || '').trim(),
        };
        formik.setFieldValue('offers', [...offers, newOffer]);
        // Новий офер додається в кінець — перестрибуємо на останню сторінку,
        // щоб одразу його було видно (фільтр при цьому скидаємо).
        setLocalOfferFilters({});
        setOfferPage(Math.ceil((offers.length + 1) / OFFERS_PER_PAGE));
    };

    const handleRemoveOffer = (indexToRemove) => {
        const newOffers = offers.filter((_, i) => i !== indexToRemove);
        formik.setFieldValue('offers', newOffers);
    };

    // Ручное редактирование артикула: помечаем offer.skuManual, чтобы
    // авто-генерация больше не перезаписывала поле (в т.ч. после очистки).
    // ВАЖНО: меняем оффер одним setFieldValue (атомарно), иначе два подряд
    // setFieldValue по разным полям могут затереть друг друга и skuManual не
    // зафиксируется — тогда пустой артикул генерируется заново.
    const handleSkuChange = (offerIndex, e) => {
        const value = e?.target?.value ?? '';
        const nextOffers = (formik.values.offers || []).map((offer, i) =>
            i === offerIndex ? { ...offer, sku: value, skuManual: true } : offer
        );
        formik.setFieldValue('offers', nextOffers);
    };

    // Live SKU duplicate check on blur: in-form duplicates + cross-product (server).
    const handleSkuBlur = async (offerIndex) => {
        const currentOffers = formik.values.offers || [];
        const offer = currentOffers[offerIndex];
        const sku = String(offer?.sku || '').trim();
        if (!sku) return;

        const duplicateInForm = currentOffers.some(
            (o, i) => i !== offerIndex && String(o?.sku || '').trim() === sku
        );
        if (duplicateInForm) {
            toast.error(`Артикул «${sku}» повторюється у двох варіантах цього товару. Змініть його.`);
            return;
        }

        const { duplicate, conflict } = await checkAdminCatalogOfferSku(sku, offer?._id || null);
        if (duplicate) {
            const where = conflict?.groupTitle
                ? `у товарі «${conflict.groupTitle}»`
                : 'в іншому товарі';
            toast.error(`Артикул «${sku}» вже використовується ${where}. Змініть артикул.`);
        }
    };

    const getOfferImages = (offer) => {
        const list = [];
        const sourceImages = Array.isArray(offer?.images) ? offer.images : [];

        sourceImages.forEach((img) => {
            const normalized = String(img || '').trim();
            if (!normalized || list.includes(normalized)) return;
            list.push(normalized);
        });

        const fallbackImage = String(offer?.image || '').trim();
        if (fallbackImage && !list.includes(fallbackImage)) {
            list.unshift(fallbackImage);
        }

        return list.slice(0, MAX_OFFER_IMAGES);
    };

    const getOfferImageSlots = (offer) => {
        const images = getOfferImages(offer);
        if (images.length >= MAX_OFFER_IMAGES) {
            return images;
        }

        return [...images, ''];
    };

    const setOfferImages = (offerIndex, nextImages) => {
        const normalizedImages = (Array.isArray(nextImages) ? nextImages : [])
            .map((img) => String(img || '').trim())
            .filter(Boolean)
            .filter((img, idx, arr) => arr.indexOf(img) === idx)
            .slice(0, MAX_OFFER_IMAGES);

        formik.setFieldValue(`offers[${offerIndex}].images`, normalizedImages, false);
        formik.setFieldValue(`offers[${offerIndex}].image`, normalizedImages[0] || '', false);
    };

    const setOfferImageByIndex = (offerIndex, imageIndex, imageValue) => {
        const currentImages = getOfferImages(formik.values.offers?.[offerIndex]);
        const nextImages = [...currentImages];

        while (nextImages.length <= imageIndex) {
            nextImages.push('');
        }

        nextImages[imageIndex] = String(imageValue || '').trim();
        setOfferImages(offerIndex, nextImages);
    };

    const removeOfferImage = (offerIndex, imageIndex) => {
        const currentImages = getOfferImages(formik.values.offers?.[offerIndex]);
        const nextImages = currentImages.filter((_, idx) => idx !== imageIndex);
        setOfferImages(offerIndex, nextImages);
    };

    // Перетягивание фото оффера для смены порядка (порядок попадает в offer.images
    // и так же отображается в галерее на странице товара).
    const dragMediaRef = useRef(null);

    const moveOfferImage = (offerIndex, fromIndex, toIndex) => {
        const currentImages = getOfferImages(formik.values.offers?.[offerIndex]);
        if (
            fromIndex === toIndex ||
            fromIndex < 0 ||
            toIndex < 0 ||
            fromIndex >= currentImages.length ||
            toIndex >= currentImages.length
        ) {
            return;
        }
        const nextImages = [...currentImages];
        const [moved] = nextImages.splice(fromIndex, 1);
        nextImages.splice(toIndex, 0, moved);
        setOfferImages(offerIndex, nextImages);
    };

    const applyCardImageToAllOffers = (rawImageUrl) => {
        const cardImage = String(rawImageUrl || '').trim();
        const nextOffers = offers.map((offer) => {
            const existingImages = getOfferImages(offer);
            const withoutCardDuplicate = existingImages.filter((img) => img !== cardImage);
            const normalizedImages = cardImage
                ? [cardImage, ...withoutCardDuplicate].slice(0, MAX_OFFER_IMAGES)
                : withoutCardDuplicate.slice(0, MAX_OFFER_IMAGES);

            return {
                ...offer,
                images: normalizedImages,
                image: normalizedImages[0] || '',
            };
        });

        formik.setFieldValue('offers', nextOffers);
    };

    // === ЛОГІКА ЗНИЖОК ОФФЕРІВ ===
    const setOfferDiscountType = (offerIndex, nextType) => {
        formik.setFieldValue(`offers[${offerIndex}].discountType`, nextType, false);
        // обнуляємо значення протилежного типу, щоб не лишалось «фантомної» знижки
        if (nextType !== 'percent') formik.setFieldValue(`offers[${offerIndex}].discount`, 0, false);
        if (nextType !== 'amount') formik.setFieldValue(`offers[${offerIndex}].discountUAH`, 0, false);
    };

    const applyDiscountToAllOffers = () => {
        const type = DISCOUNT_TYPE_OPTIONS.some((opt) => opt.value === bulkDiscountType)
            ? bulkDiscountType
            : 'none';
        const numericValue = Math.max(0, Number(bulkDiscountValue) || 0);

        const nextOffers = offers.map((offer) => ({
            ...offer,
            discountType: type,
            discount: type === 'percent' ? numericValue : 0,
            discountUAH: type === 'amount' ? numericValue : 0,
        }));

        formik.setFieldValue('offers', nextOffers);
    };

    const applyPriceToAllOffers = () => {
        const price = String(bulkPriceValue || '').trim();
        if (price === '') return;

        const nextOffers = offers.map((offer) => ({
            ...offer,
            price,
        }));

        formik.setFieldValue('offers', nextOffers);
    };

    // Залишки з KeyCRM тепер підтягуються автоматично при збереженні товару
    // (для всіх оферів — нових і вже створених) у useProductFormSubmit.

    // === МАСОВА ЗМІНА МАТЕРІАЛУ (вісь A7) ДЛЯ ВСІХ ОФФЕРІВ ===
    const materialAxis = useMemo(
        () => axes.find((axis) => axis.axisId === MATERIAL_AXIS_ID) || null,
        [axes]
    );

    const materialOptions = useMemo(() => {
        if (!materialAxis) return [];
        return (materialAxis.valuesPreset || []).map((val) => ({
            value: getAxisPresetValue(val),
            label: getAxisPresetLabel(val),
        }));
    }, [materialAxis]);

    const applyMaterialToAllOffers = () => {
        const value = String(bulkMaterialValue || '').trim();
        const nextOffers = offers.map((offer) => ({
            ...offer,
            options: {
                ...(offer.options || {}),
                [MATERIAL_AXIS_ID]: value,
            },
        }));

        formik.setFieldValue('offers', nextOffers);
    };

    // === ВІДЕО ДЛЯ КОЛЬОРУ (вісь A1): застосувати до всіх оферів цього кольору ===
    const colorAxis = useMemo(
        () => axes.find((axis) => axis.axisId === COLOR_AXIS_ID) || null,
        [axes]
    );

    const colorVideoOptions = useMemo(() => {
        const present = new Set(
            offers.map((o) => String(o?.options?.[COLOR_AXIS_ID] || '').trim()).filter(Boolean)
        );
        const fromAxis = (colorAxis?.valuesPreset || []).map((val) => ({
            value: getAxisPresetValue(val),
            label: getAxisPresetLabel(val),
        }));
        const known = new Set(fromAxis.map((o) => o.value));
        const extras = [...present].filter((v) => !known.has(v)).map((v) => ({ value: v, label: v }));
        return [...fromAxis.filter((o) => present.has(o.value)), ...extras];
    }, [colorAxis, offers]);

    // === ПЕР-КОЛІР: відео + пріоритет (drag&drop) ===
    const colorVideoUrlOf = (color) => {
        for (const o of offers) {
            if (String(o?.options?.[COLOR_AXIS_ID] || '').trim() !== color) continue;
            const v = getOfferImages(o).find((u) => isVideoMediaUrl(u));
            if (v) return v;
        }
        return '';
    };

    const applyVideoToColor = (color, video) => {
        const c = String(color || '').trim();
        const v = String(video || '').trim();
        if (!c || !v) return 0;
        let count = 0;
        const nextOffers = offers.map((offer) => {
            if (String(offer?.options?.[COLOR_AXIS_ID] || '').trim() !== c) return offer;
            count += 1;
            const photos = getOfferImages(offer).filter((u) => !isVideoMediaUrl(u));
            const nextImages = [...photos.slice(0, MAX_OFFER_IMAGES - 1), v];
            return { ...offer, images: nextImages, image: nextImages[0] || '' };
        });
        if (count) formik.setFieldValue('offers', nextOffers);
        return count;
    };

    const removeVideoFromColor = (color) => {
        const c = String(color || '').trim();
        let count = 0;
        const nextOffers = offers.map((offer) => {
            if (String(offer?.options?.[COLOR_AXIS_ID] || '').trim() !== c) return offer;
            const all = getOfferImages(offer);
            const photos = all.filter((u) => !isVideoMediaUrl(u));
            if (photos.length === all.length) return offer;
            count += 1;
            return { ...offer, images: photos, image: photos[0] || '' };
        });
        if (count) {
            formik.setFieldValue('offers', nextOffers);
            toast.success(`Відео прибрано (${count} оф.)`);
        }
    };

    // === ПЕР-КОЛІР: ФОТО (однакові на всі розміри кольору) ===
    // Фото першого розміру кольору, де вони є (без відео).
    const colorPhotosOf = (color) => {
        const c = String(color || '').trim();
        for (const o of offers) {
            if (String(o?.options?.[COLOR_AXIS_ID] || '').trim() !== c) continue;
            const photos = getOfferImages(o).filter((u) => !isVideoMediaUrl(u));
            if (photos.length) return photos;
        }
        return [];
    };

    // Застосовує однаковий набір медіа до всіх розмірів кольору: спершу до
    // завантажених оферів у формі (миттєво видно), потім на сервері — щоб
    // охопити всі розміри кольору навіть поза поточною сторінкою пагінації.
    const applyColorMedia = async (color, mediaList) => {
        const c = String(color || '').trim();
        if (!c) return;

        const cleaned = (Array.isArray(mediaList) ? mediaList : [])
            .map((u) => String(u || '').trim())
            .filter(Boolean);
        // Фото — першими, відео — останнім (так очікує галерея і поле image).
        const photos = cleaned.filter((u) => !isVideoMediaUrl(u));
        const videos = cleaned.filter((u) => isVideoMediaUrl(u));
        const images = [...photos, ...videos].slice(0, MAX_OFFER_IMAGES);
        const primary = photos[0] || images[0] || '';

        let loadedCount = 0;
        const nextOffers = offers.map((offer) => {
            if (String(offer?.options?.[COLOR_AXIS_ID] || '').trim() !== c) return offer;
            loadedCount += 1;
            return { ...offer, images, image: primary };
        });
        if (loadedCount) formik.setFieldValue('offers', nextOffers);

        // На сервері — лише для вже збереженого товару (офери мають _id у БД).
        if (isEditMode && initialData?._id) {
            try {
                const res = await applyAdminCatalogOfferImagesByColor(initialData._id, {
                    axisId: COLOR_AXIS_ID,
                    value: c,
                    images,
                });
                const n = res?.matched ?? loadedCount;
                toast.success(`Фото застосовано до ${n} розмір(ів) кольору`);
                return;
            } catch (e) {
                console.error('apply images by color failed', e);
                toast.error(e?.message || 'Не вдалося застосувати фото на сервері');
                return;
            }
        }
        toast.success(`Фото застосовано до ${loadedCount} розмір(ів) кольору`);
    };

    // Кнопка «скопіювати фото з розміру»: бере фото першого розміру кольору
    // (+ відео цього кольору, якщо є) і ставить ідентичний набір усім розмірам.
    const applyPhotosFromSizeToColor = async (color) => {
        const photos = colorPhotosOf(color);
        if (!photos.length) {
            toast.error('Спочатку додайте фото хоча б одному розміру цього кольору');
            return;
        }
        const video = colorVideoUrlOf(color);
        await applyColorMedia(color, video ? [...photos, video] : photos);
    };

    const [colorPhotoUploading, setColorPhotoUploading] = useState('');
    // Завантажувач фото для кольору: вантажить набір фото та застосовує до всіх
    // розмірів кольору (зберігаючи наявне відео кольору).
    const uploadPhotosForColor = async (color, files) => {
        const list = files ? Array.from(files) : [];
        if (!list.length) return;
        if (list.some((f) => isVideoFile(f))) {
            toast.error('Тут лише фото. Відео завантажуйте через кнопку відео.');
            return;
        }
        setColorPhotoUploading(color);
        try {
            const response = await uploadAdminImages(list);
            const urls = (response?.data || response?.files || [])
                .map((f) => getFullImageUrl(f?.url))
                .filter(Boolean);
            if (!urls.length) { toast.error('Не вдалося отримати URL фото'); return; }
            const video = colorVideoUrlOf(color);
            await applyColorMedia(color, video ? [...urls, video] : urls);
        } catch (e) {
            console.error('Color photos upload error:', e);
            toast.error('Помилка завантаження фото');
        } finally {
            setColorPhotoUploading('');
        }
    };

    const [colorUploading, setColorUploading] = useState('');
    const [videoPreview, setVideoPreview] = useState(null); // {url, label} для модалки перегляду
    const uploadVideoForColor = async (color, file) => {
        if (!file) return;
        if (!isVideoFileWithinLimit(file)) return;
        setColorUploading(color);
        try {
            const response = await uploadAdminVideo(file, { transcode: false });
            const url = getFullImageUrl(response?.file?.url || response?.url || response?.data?.[0]?.url);
            if (!url) { toast.error('Не вдалося отримати URL відео'); return; }
            const n = applyVideoToColor(color, url);
            toast.success(`Відео додано до ${n} оф. кольору`);
        } catch (e) {
            console.error('Color video upload error:', e);
            toast.error('Помилка завантаження відео');
        } finally {
            setColorUploading('');
        }
    };

    // Пріоритет кольорів: спочатку за збереженим colorOrder, потім решта присутніх.
    const priorityColors = useMemo(() => {
        const present = colorVideoOptions;
        const order = formik.values.colorOrder || [];
        const byVal = new Map(present.map((c) => [c.value, c]));
        const ordered = [];
        const seen = new Set();
        for (const v of order) if (byVal.has(v) && !seen.has(v)) { ordered.push(byVal.get(v)); seen.add(v); }
        for (const c of present) if (!seen.has(c.value)) { ordered.push(c); seen.add(c.value); }
        return ordered;
    }, [colorVideoOptions, formik.values.colorOrder]);

    const colorDragRef = useRef(null);
    const moveColor = (fromIdx, toIdx) => {
        if (fromIdx == null || toIdx == null || fromIdx === toIdx) return;
        const vals = priorityColors.map((c) => c.value);
        const [m] = vals.splice(fromIdx, 1);
        vals.splice(toIdx, 0, m);
        formik.setFieldValue('colorOrder', vals);
    };

    const cardImageFromOffers = useMemo(
        () => getOfferImages(offers?.[0])[0] || '',
        [offers]
    );

    // Заглавне фото картки — окреме поле (imageURL). Беремо його з БД; перше фото
    // першого офера лише як дефолт для НОВИХ товарів (коли imageURL ще порожній).
    // Так картка більше не перезаписується фото офера при кожній зміні.
    useEffect(() => {
        setCardImageDraft(formik.values.imageURL || cardImageFromOffers || '');
    }, [cardImageFromOffers, formik.values.imageURL]);

    // Синхронізуємо formik.values.imageURL із тим, що бачить користувач у превʼю карточки.
    useEffect(() => {
        if (!cardImageDraft) return;
        if (cardImageDraft === formik.values.imageURL) return;
        formik.setFieldValue('imageURL', cardImageDraft, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardImageDraft]);

    const handleCardImageUpload = async (e) => {
        const inputEl = e.currentTarget;
        const file = inputEl?.files?.[0];
        if (!file) return;

        setIsCardImageUploading(true);

        try {
            const response = await uploadAdminImages(file);
            const uploadedUrl = getFullImageUrl(response?.data?.[0]?.url);

            if (uploadedUrl) {
                // Фото картки — окреме поле; в офери його НЕ пушимо (розв'язка).
                setCardImageDraft(uploadedUrl);
            } else {
                toast.error('Не вдалося отримати URL завантаженого фото');
            }
        } catch (error) {
            console.error('Card image upload error:', error);
                toast.error('Помилка при завантаженні фото карточки товару');
        } finally {
            setIsCardImageUploading(false);
            if (inputEl) {
                inputEl.value = '';
            }
        }
    };

    const handleGroupVideoUpload = async (e) => {
        const inputEl = e.currentTarget;
        const file = inputEl?.files?.[0];
        if (!file) return;
        if (!isVideoFileWithinLimit(file)) { if (inputEl) inputEl.value = ''; return; }

        setIsGroupVideoUploading(true);

        try {
            const response = await uploadAdminVideo(file, { transcode: false });
            console.log('Group video upload response:', response);
            const uploadedUrl =
                response?.file?.url ||
                response?.url ||
                response?.files?.[0]?.url ||
                response?.data?.[0]?.url ||
                '';

            console.log('Group video uploaded URL:', uploadedUrl);

            if (response?.ok && uploadedUrl) {
                formik.setFieldValue('videoURL', getFullImageUrl(uploadedUrl));
            } else {
                toast.error('Не вдалося отримати URL завантаженого відео');
            }
        } catch (error) {
            console.error('Video upload error:', error);
            toast.error('Помилка при завантаженні відео');
        } finally {
            setIsGroupVideoUploading(false);
            if (inputEl) {
                inputEl.value = '';
            }
        }
    };

    return (
        <>
        <form id="product-create-form" onSubmit={formik.handleSubmit} className="product-form">
            <UnfilledAxesModal
                data={unfilledModal}
                onCancel={() => resolveUnfilledModal(false)}
                onConfirm={() => resolveUnfilledModal(true)}
            />
            {formik.isSubmitting && !unfilledModal && (
                <AdminPageLoader label="Збереження товару..." fullScreen />
            )}
             <div className="product__variations-header">
                 <h1>Дані товару</h1>
                  <div className="form-translate-bar">
                <TranslateButton
                    isLoading={isTranslating}
                    onClick={() => {
                        translateFields([
                            { from: 'title.ua',       to: 'title.en' },
                            { from: 'subtitle.ua',    to: 'subtitle.en' },
                            { from: 'description.ua', to: 'description.en' },
                            // Шлях через крапку, бо getByPath в useAutoTranslate
                            // розбиває рядок тільки по крапках і дужок не знає.
                            ...(formik.values.specRows || []).flatMap((_, i) => [
                                { from: `specRows.${i}.title.ua`, to: `specRows.${i}.title.en` },
                                { from: `specRows.${i}.value.ua`, to: `specRows.${i}.value.en` },
                            ]),
                            ...(formik.values.contentSections || []).flatMap((_, i) => [
                                { from: `contentSections.${i}.title.ua`,   to: `contentSections.${i}.title.en` },
                                { from: `contentSections.${i}.content.ua`, to: `contentSections.${i}.content.en` },
                            ]),
                        ]);
                    }}
                />
            </div>
            </div>
           

           
            <div className="form-wrapper-2-column">
                <CustomInput
                    id="title.ua" name="title.ua" label="Назва товару (UA)"
                    value={formik.values.title?.ua || ''} onChange={formik.handleChange} onBlur={formik.handleBlur}
                    placeholder="Введіть назву українською" error={formik.errors.title?.ua} touched={formik.touched.title?.ua}
                />
                <CustomInput
                    id="title.en" name="title.en" label="Назва товару (EN)"
                    value={formik.values.title?.en || ''} onChange={formik.handleChange} onBlur={formik.handleBlur}
                    placeholder="Enter title in English" error={formik.errors.title?.en} touched={formik.touched.title?.en}
                />
            </div>

            <div className="form-wrapper-2-1-column">
                <CustomInput
                    id="slug" name="slug" label="Slug (URL товару)"
                    value={formik.values.slug || ''} onChange={formik.handleChange} onBlur={formik.handleBlur}
                    placeholder="generyetsya-avtomatichno" error={formik.errors.slug} touched={formik.touched.slug}
                />
                <div className="form-group">
                    <label htmlFor="category">Категорія</label>
                    <div className="input-wrapper">
                        <CustomSelect
                            options={categoryOptions} 
                            value={formik.values.categoryIds || []}
                            onChange={(value) => formik.setFieldValue('categoryIds', value)} 
                            onBlur={formik.handleBlur}
                            name="categoryIds"
                            id="category"
                            error={formik.errors.categoryIds}
                            touched={formik.touched.categoryIds}
                            placeholder={categoryOptions.length > 0 ? "Виберіть категорію" : "Завантаження..."}
                            isMulti
                        />
                    </div>
                    {formik.touched.categoryIds && formik.errors.categoryIds ? (
                        <div className="error-text">{formik.errors.categoryIds}</div>
                    ) : null}
                </div>
            </div>

            <div className="form-group">
                <label htmlFor="status">Статус</label>
                <div className="input-wrapper">
                    <CustomSelect
                        options={STATUS_OPTIONS}
                        value={formik.values.status}
                        onChange={(value) => formik.setFieldValue('status', value)}
                        onBlur={formik.handleBlur}
                        name="status"
                        id="status"
                        error={formik.errors.status}
                        touched={formik.touched.status}
                        placeholder="Виберіть статус"
                    />
                </div>
            </div>

            <div className="form-wrapper-2-column">
                <div />
                <div className="form-group">
                    <label htmlFor="groupAccessoryGroupId">Товар для допродажу для всіх офферів</label>
                    <div className={`input-wrapper ${isAccessorySelectionDisabled ? 'accessory-select-lock' : ''}`}>
                        <CustomSelect
                            options={accessoryOptions}
                            value={formik.values.groupAccessoryGroupId || ''}
                            onChange={applyAccessoryToAllOffers}
                            onInputChange={setAccessorySearchQuery}
                            name="groupAccessoryGroupId"
                            id="groupAccessoryGroupId"
                            isDisabled={isAccessorySelectionDisabled}
                            placeholder={
                                isAccessorySelectionDisabled
                                    ? 'Спочатку створіть оффери'
                                    : (isAccessorySearchLoading ? 'Пошук товарів...' : 'Оберіть товар')
                            }
                        />
                    </div>
                    {isAccessorySelectionDisabled && (
                        <div className="accessory-select-lock__hint">Спочатку потрібно створити оффери.</div>
                    )}
                </div>
            </div>

            {/* === ОСНОВНА ІНФОРМАЦІЯ ПРО ТОВАР === */}
            <div className="form-wrapper-2-column">
                <CustomInput
                    id="subtitle.ua" name="subtitle.ua" label="Підпис до товару (UA)"
                    value={formik.values.subtitle?.ua || ''} onChange={formik.handleChange} onBlur={formik.handleBlur}
                    placeholder="Короткий підпис українською..." error={formik.errors.subtitle?.ua} touched={formik.touched.subtitle?.ua}
                />
                <CustomInput
                    id="subtitle.en" name="subtitle.en" label="Підпис до товару (EN)"
                    value={formik.values.subtitle?.en || ''} onChange={formik.handleChange} onBlur={formik.handleBlur}
                    placeholder="Short subtitle in English..." error={formik.errors.subtitle?.en} touched={formik.touched.subtitle?.en}
                />
            </div>

            {/* === ОПИС ТОВАРУ === */}
            <div className="form-group">
                <label htmlFor="description.ua">Опис товару (UA)</label>
                <textarea
                    id="description.ua"
                    name="description.ua"
                    className={`custom-textarea ${formik.touched.description?.ua && formik.errors.description?.ua ? 'error' : ''}`}
                    value={formik.values.description?.ua || ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Детальний опис українською..."
                />
                {formik.touched.description?.ua && formik.errors.description?.ua && (
                    <div className="error-text">{formik.errors.description.ua}</div>
                )}
            </div>

            <div className="form-group">
                <label htmlFor="description.en">Опис товару (EN)</label>
                <textarea
                    id="description.en"
                    name="description.en"
                    className={`custom-textarea ${formik.touched.description?.en && formik.errors.description?.en ? 'error' : ''}`}
                    value={formik.values.description?.en || ''}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder="Detailed description in English..."
                />
                {formik.touched.description?.en && formik.errors.description?.en && (
                    <div className="error-text">{formik.errors.description.en}</div>
                )}
            </div>

            {/* === ТАБЛИЦЯ ХАРАКТЕРИСТИК === */}
            <div className="form-group">
                <label className="form-label">Таблиця характеристик</label>

                <p style={{ margin: '0 0 12px', fontSize: 12, lineHeight: 1.5, color: '#1E40AF', background: '#DBEAFE', border: '1px solid #BFDBFE', borderRadius: 8, padding: '8px 10px' }}>
                    ℹ️ Довільні рядки «назва — значення» для блоку «Таблиця характеристик» на сторінці товару
                    (напр. «Сезон — Демісезон», «Довжина рукава — Довгий»). Якщо рядків немає, блок на сторінці не показується.
                    Склад і догляд сюди дублювати не треба — для них є окремі блоки нижче.
                </p>

                {(formik.values.specRows || []).map((row, index) => (
                    <div key={index} className="form-wrapper-2-column" style={{ border: '1px solid #E5E7EB', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                        <CustomInput
                            id={`specRows.${index}.title.ua`}
                            name={`specRows.${index}.title.ua`}
                            label="Назва (UA)"
                            value={row.title?.ua || ''}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="Напр. Сезон"
                        />
                        <CustomInput
                            id={`specRows.${index}.title.en`}
                            name={`specRows.${index}.title.en`}
                            label="Назва (EN)"
                            value={row.title?.en || ''}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="e.g. Season"
                        />
                        <CustomInput
                            id={`specRows.${index}.value.ua`}
                            name={`specRows.${index}.value.ua`}
                            label="Значення (UA)"
                            value={row.value?.ua || ''}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="Напр. Демісезон"
                        />
                        <CustomInput
                            id={`specRows.${index}.value.en`}
                            name={`specRows.${index}.value.en`}
                            label="Значення (EN)"
                            value={row.value?.en || ''}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="e.g. Mid-season"
                        />
                        <button
                            type="button"
                            className="btn-remove"
                            onClick={() => {
                                const next = [...(formik.values.specRows || [])];
                                next.splice(index, 1);
                                formik.setFieldValue('specRows', next);
                            }}
                        >
                            Видалити рядок
                        </button>
                    </div>
                ))}

                <button
                    type="button"
                    className="btn-add-primary"
                    onClick={() => {
                        formik.setFieldValue('specRows', [
                            ...(formik.values.specRows || []),
                            createSpecRow(),
                        ]);
                    }}
                >
                    + Додати рядок
                </button>
            </div>

            {/* === ДОДАТКОВІ БЛОКИ НА СТОРІНЦІ ТОВАРУ === */}
            <div className="form-group">
                <label className="form-label">Додаткові блоки на сторінці товару</label>

                <p style={{ margin: '0 0 12px', fontSize: 12, lineHeight: 1.5, color: '#1E40AF', background: '#DBEAFE', border: '1px solid #BFDBFE', borderRadius: 8, padding: '8px 10px' }}>
                    ℹ️ Блок «Опис» на сторінці товару береться з поля «Опис товару» вище — окремо його заповнювати не треба.
                    Тут додаються решта блоків-акордеонів: склад, догляд тощо.
                </p>

                {(formik.values.contentSections || []).map((section, index) => (
                    <div key={index} className="form-group" style={{ border: '1px solid #E5E7EB', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                        <div className="form-wrapper-2-column">
                            <CustomInput
                                id={`contentSections.${index}.title.ua`}
                                name={`contentSections.${index}.title.ua`}
                                label="Заголовок блоку (UA)"
                                value={section.title?.ua || ''}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder="Напр. Склад"
                            />
                            <CustomInput
                                id={`contentSections.${index}.title.en`}
                                name={`contentSections.${index}.title.en`}
                                label="Заголовок блоку (EN)"
                                value={section.title?.en || ''}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder="e.g. Composition"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor={`contentSections.${index}.content.ua`}>Текст блоку (UA)</label>
                            <textarea
                                id={`contentSections.${index}.content.ua`}
                                name={`contentSections.${index}.content.ua`}
                                className="custom-textarea"
                                value={section.content?.ua || ''}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder="Напр. 65% віскоза, 33% поліестер, 2% еластан"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor={`contentSections.${index}.content.en`}>Текст блоку (EN)</label>
                            <textarea
                                id={`contentSections.${index}.content.en`}
                                name={`contentSections.${index}.content.en`}
                                className="custom-textarea"
                                value={section.content?.en || ''}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder="e.g. 65% viscose, 33% polyester, 2% elastane"
                            />
                        </div>

                        <button
                            type="button"
                            className="btn-remove"
                            onClick={() => {
                                const next = [...(formik.values.contentSections || [])];
                                next.splice(index, 1);
                                formik.setFieldValue('contentSections', next);
                            }}
                        >
                            Видалити блок
                        </button>
                    </div>
                ))}

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {CONTENT_SECTION_PRESETS.map((preset) => {
                        const used = (formik.values.contentSections || []).some(
                            (s) => String(s?.key || '').trim() === preset.key,
                        );
                        return (
                            <button
                                key={preset.key}
                                type="button"
                                className="btn-add-primary"
                                disabled={used}
                                title={used ? 'Такий блок уже доданий' : ''}
                                onClick={() => {
                                    formik.setFieldValue('contentSections', [
                                        ...(formik.values.contentSections || []),
                                        createContentSection(preset),
                                    ]);
                                }}
                            >
                                + {preset.title.ua}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="form-group">
                <label className="form-label" htmlFor="group-video-url">Відео групи товару</label>

                <p style={{ margin: '0 0 10px', fontSize: 12, lineHeight: 1.5, color: '#B45309', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 10px' }}>
                    💡 {VIDEO_ADVICE}
                </p>

                {formik.values.videoURL ? (
                    <video
                        src={getFullImageUrl(formik.values.videoURL)}
                        controls
                        preload="metadata"
                        style={{
                            width: '100%',
                            maxHeight: '260px',
                            objectFit: 'contain',
                            borderRadius: '8px',
                            marginBottom: '12px',
                            background: '#0F172A'
                        }}
                    />
                ) : null}

                <div
                    className="size-chart-upload__box"
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        padding: '15px',
                        minHeight: '92px',
                        textAlign: 'center',
                        border: '1px dashed #ccc',
                        borderRadius: '8px',
                        opacity: isGroupVideoUploading ? 0.7 : 1
                    }}
                >
                    <span style={{ color: '#FF99D6', fontWeight: '500' }}>
                        {isGroupVideoUploading
                            ? 'Завантаження відео...'
                            : (formik.values.videoURL ? 'Змінити відео' : 'Завантажити відео')}
                    </span>

                    <input
                        type="file"
                        accept="video/*"
                        multiple={false}
                        disabled={isGroupVideoUploading}
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            opacity: 0,
                            cursor: isGroupVideoUploading ? 'wait' : 'pointer'
                        }}
                        onChange={handleGroupVideoUpload}
                    />
                </div>

                <input
                    id="group-video-url"
                    type="text"
                    placeholder="Або вставте URL відео..."
                    value={formik.values.videoURL || ''}
                    onChange={(e) => formik.setFieldValue('videoURL', e.target.value)}
                    style={{
                        marginTop: '8px',
                        width: '100%',
                        padding: '6px 10px',
                        fontSize: '12px',
                        border: '1px solid #CBD5E1',
                        borderRadius: '6px',
                        color: '#475569',
                        outline: 'none',
                        boxSizing: 'border-box'
                    }}
                />

                {formik.values.videoURL ? (
                    <button
                        type="button"
                        className="variation-image-upload__remove-btn"
                        onClick={() => formik.setFieldValue('videoURL', '')}
                    >
                        Видалити відео
                    </button>
                ) : null}
            </div>

            {/* === РОЗМІРНА СІТКА === */}
            <div className="form-group">
                <label className="form-label">Розмірна сітка</label>
                <div 
                    className="size-chart-upload__box" 
                    style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        position: 'relative',
                        padding: '15px',
                        textAlign: 'center',
                        border: '1px dashed #ccc',
                        borderRadius: '8px'
                    }}
                >
                    {formik.values.sizeChart?.imageUrl ? (
                        <>
                            <img 
                                src={getFullImageUrl(formik.values.sizeChart.imageUrl)} 
                                alt="Size Chart Preview" 
                                style={{ 
                                    width: '100%', 
                                    maxHeight: '200px', 
                                    objectFit: 'contain', 
                                    borderRadius: '4px', 
                                    marginBottom: '8px' 
                                }} 
                            />
                            <span style={{ color: '#FF99D6', fontWeight: '500' }}>
                                Змінити картинку
                            </span>
                        </>
                    ) : (
                        <>
                            <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44" fill="none" style={{ marginBottom: '8px' }}>
                                <path d="M39.1111 4.88889V39.1111H4.88889V4.88889H39.1111ZM39.1111 0H4.88889C2.2 0 0 2.2 0 4.88889V39.1111C0 41.8 2.2 44 4.88889 44H39.1111C41.8 44 44 41.8 44 39.1111V4.88889C44 2.2 41.8 0 39.1111 0ZM27.2311 21.6578L19.8978 31.1178L14.6667 24.7867L7.33333 34.2222H36.6667L27.2311 21.6578Z" fill="#FF99D6"/>
                            </svg>
                            <span style={{ color: '#FF99D6', fontWeight: '500' }}>
                                Завантажити розмірну сітку
                            </span>
                        </>
                    )}
                    
                    <input
                        type="file"
                        accept="image/jpeg, image/png, image/webp, image/gif, image/svg+xml, image/avif, image/tiff, image/bmp, image/heic, image/heif, .heic, .heif"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            opacity: 0,
                            cursor: 'pointer'
                        }}
                        onChange={async (e) => {
                            const file = e.currentTarget.files[0];
                            if (!file) return;

                            try {
                                const response = await uploadAdminImages(file);

                                if (response?.ok && response?.data?.length > 0) {
                                    const imageUrl = response.data[0].url; 
                                    const fullImageUrl = getFullImageUrl(imageUrl);
                                    formik.setFieldValue('sizeChart.imageUrl', fullImageUrl);
                                }
                            } catch (error) {
                                console.error("Upload error:", error);
                                toast.error("Помилка при завантаженні картинки розмірної сітки");
                            }
                        }}
                    />
                </div>
                <input
                    type="text"
                    placeholder="Або вставте URL..."
                    value={formik.values.sizeChart?.imageUrl || ''}
                    onChange={(e) => formik.setFieldValue('sizeChart.imageUrl', e.target.value)}
                    style={{
                        marginTop: '8px',
                        width: '100%',
                        padding: '6px 10px',
                        fontSize: '12px',
                        border: '1px solid #CBD5E1',
                        borderRadius: '6px',
                        color: '#475569',
                        outline: 'none',
                        boxSizing: 'border-box'
                    }}
                />
            </div>

            {/* === ВАРІАЦІЇ ТОВАРУ (ОФФЕРИ) === */}
            <div className="product__variations-header">
                <h1>Варіації товару (Оффери)</h1>
            </div>

            {offers.length > 0 && (
                <div className="form-group">
                    <label className="form-label" htmlFor="card-image-url">
                        Фото карточки товару (1-ше фото для всіх офферів)
                    </label>

                    {cardImageDraft ? (
                        <img
                            src={getFullImageUrl(cardImageDraft)}
                            alt="Card image preview"
                            style={{
                                width: '100%',
                                maxHeight: '220px',
                                objectFit: 'contain',
                                borderRadius: '8px',
                                marginBottom: '12px',
                                border: '1px solid #E2E8F0'
                            }}
                        />
                    ) : null}

                    <div
                        className="size-chart-upload__box"
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            padding: '15px',
                            minHeight: '92px',
                            textAlign: 'center',
                            border: '1px dashed #ccc',
                            borderRadius: '8px',
                            opacity: isCardImageUploading ? 0.7 : 1
                        }}
                    >
                        <span style={{ color: '#FF99D6', fontWeight: '500' }}>
                            {isCardImageUploading
                                ? 'Завантаження фото...'
                                : 'Завантажити фото карточки'}
                        </span>

                        <input
                            type="file"
                            accept="image/jpeg, image/png, image/webp, image/gif, image/svg+xml, image/avif, image/tiff, image/bmp, image/heic, image/heif, .heic, .heif"
                            multiple={false}
                            disabled={isCardImageUploading}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                opacity: 0,
                                cursor: isCardImageUploading ? 'wait' : 'pointer'
                            }}
                            onChange={handleCardImageUpload}
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                        <input
                            id="card-image-url"
                            type="text"
                            placeholder="Або вставте URL фото карточки..."
                            value={cardImageDraft}
                            onChange={(e) => setCardImageDraft(e.target.value)}
                            style={{
                                flex: '1 1 360px',
                                minWidth: '260px',
                                padding: '6px 10px',
                                fontSize: '12px',
                                border: '1px solid #CBD5E1',
                                borderRadius: '6px',
                                color: '#475569',
                                outline: 'none',
                                boxSizing: 'border-box'
                            }}
                        />

                        <button
                            type="button"
                            className="btn-add-primary"
                            onClick={() => applyCardImageToAllOffers(cardImageDraft)}
                            disabled={!String(cardImageDraft || '').trim()}
                            style={{
                                padding: '10px 16px',
                                backgroundColor: '#FF4FA3',
                                color: '#FFFFFF',
                                border: 'none',
                                marginTop: '0',
                                borderRadius: '8px',
                                fontWeight: 600,
                                cursor: !String(cardImageDraft || '').trim() ? 'not-allowed' : 'pointer',
                                opacity: !String(cardImageDraft || '').trim() ? 0.6 : 1
                            }}
                        >
                            Застосувати до всіх офферів
                        </button>

                        {cardImageDraft ? (
                            <button
                                type="button"
                                className="variation-image-upload__remove-btn"
                                onClick={() => {
                                    setCardImageDraft('');
                                    applyCardImageToAllOffers('');
                                }}
                            >
                                Очистити фото карточки
                            </button>
                        ) : null}
                    </div>
                </div>
            )}

            {/* === МАСОВІ ЗМІНИ ДЛЯ ВСІХ ОФФЕРІВ (майбутня модалка) === */}
            {offers.length > 0 && (
                <div
                    className="bulk-offers-edit"
                    style={{
                        margin: '8px 0 28px',
                        padding: '18px',
                        border: '1px solid #F5A8D3',
                        borderRadius: '12px',
                        background: '#FFF5FB',
                        boxShadow: '0 1px 4px rgba(255, 79, 163, 0.08)',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px',
                            flexWrap: 'wrap',
                            marginBottom: '16px',
                            paddingBottom: '12px',
                            borderBottom: '1px dashed #F5A8D3',
                        }}
                    >
                        <h3 style={{ margin: 0, fontSize: '15px', color: '#0F172A' }}>
                            Масові зміни оферів
                        </h3>
                        <span style={{ fontSize: '12px', color: '#9333EA' }}>
                            Застосовується одразу до всіх оферів товару
                        </span>
                    </div>

                    {/* --- Ціна --- */}
                    <div className="form-group">
                        <label className="form-label">Ціна для всіх оферів</label>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                            <input
                                type="number"
                                min="0"
                                placeholder="Вкажіть ціну для всіх оферів"
                                value={bulkPriceValue}
                                onChange={(e) => setBulkPriceValue(e.target.value)}
                                onWheel={(e) => e.currentTarget.blur()}
                                style={{
                                    flex: '1 1 200px',
                                    minWidth: '180px',
                                    padding: '6px 10px',
                                    fontSize: '12px',
                                    border: '1px solid #CBD5E1',
                                    borderRadius: '6px',
                                    color: '#475569',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                }}
                            />
                            <button
                                type="button"
                                className="btn-add-primary"
                                onClick={applyPriceToAllOffers}
                                disabled={!String(bulkPriceValue || '').trim()}
                                style={{
                                    padding: '10px 16px',
                                    backgroundColor: '#FF4FA3',
                                    color: '#FFFFFF',
                                    border: 'none',
                                    marginTop: '0',
                                    borderRadius: '8px',
                                    fontWeight: 600,
                                    cursor: !String(bulkPriceValue || '').trim() ? 'not-allowed' : 'pointer',
                                    opacity: !String(bulkPriceValue || '').trim() ? 0.6 : 1,
                                }}
                            >
                                Застосувати до всіх оферів
                            </button>
                        </div>
                        <span className="offer-visibility-toggle__hint">
                            Проставить одну ціну на всі офери товару. У кожному офері ціну також можна змінити окремо нижче.
                        </span>
                    </div>

                    {/* --- Знижка --- */}
                    <div className="form-group" style={{ marginTop: '16px' }}>
                        <label className="form-label">Знижка для всіх оферів</label>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                            <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
                                <CustomSelect
                                    options={DISCOUNT_TYPE_OPTIONS}
                                    value={bulkDiscountType}
                                    onChange={(val) => setBulkDiscountType(val)}
                                    name="bulkDiscountType"
                                    placeholder="Тип знижки"
                                />
                            </div>
                            <input
                                type="number"
                                min="0"
                                placeholder={bulkDiscountType === 'amount' ? 'Сума знижки (грн)' : 'Відсоток знижки (%)'}
                                value={bulkDiscountValue}
                                disabled={bulkDiscountType === 'none'}
                                onChange={(e) => setBulkDiscountValue(e.target.value)}
                                onWheel={(e) => e.currentTarget.blur()}
                                style={{
                                    flex: '1 1 200px',
                                    minWidth: '180px',
                                    padding: '6px 10px',
                                    fontSize: '12px',
                                    border: '1px solid #CBD5E1',
                                    borderRadius: '6px',
                                    color: '#475569',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                    opacity: bulkDiscountType === 'none' ? 0.6 : 1,
                                }}
                            />
                            <button
                                type="button"
                                className="btn-add-primary"
                                onClick={applyDiscountToAllOffers}
                                style={{
                                    padding: '10px 16px',
                                    backgroundColor: '#FF4FA3',
                                    color: '#FFFFFF',
                                    border: 'none',
                                    marginTop: '0',
                                    borderRadius: '8px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                Застосувати до всіх оферів
                            </button>
                        </div>
                        <span className="offer-visibility-toggle__hint">
                            Якщо тип «Без знижки» — знижку буде знято з усіх оферів.
                        </span>
                    </div>

                    {/* --- Матеріал --- */}
                    {materialAxis && (
                        <div className="form-group" style={{ marginTop: '16px' }}>
                            <label className="form-label">
                                {materialAxis.title?.ua || 'Матеріал'} для всіх оферів
                            </label>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
                                    <CustomSelect
                                        options={materialOptions}
                                        value={bulkMaterialValue}
                                        onChange={(val) => setBulkMaterialValue(val)}
                                        name="bulkMaterial"
                                        placeholder="Оберіть матеріал"
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="btn-add-primary"
                                    onClick={applyMaterialToAllOffers}
                                    disabled={!String(bulkMaterialValue || '').trim()}
                                    style={{
                                        padding: '10px 16px',
                                        backgroundColor: '#FF4FA3',
                                        color: '#FFFFFF',
                                        border: 'none',
                                        marginTop: '0',
                                        borderRadius: '8px',
                                        fontWeight: 600,
                                        cursor: !String(bulkMaterialValue || '').trim() ? 'not-allowed' : 'pointer',
                                        opacity: !String(bulkMaterialValue || '').trim() ? 0.6 : 1,
                                    }}
                                >
                                    Застосувати до всіх оферів
                                </button>
                            </div>
                            <span className="offer-visibility-toggle__hint">
                                Змінить матеріал одразу на всіх оферах. У кожному офері матеріал також можна змінити окремо нижче.
                            </span>
                        </div>
                    )}

                    {/* --- Кольори товару: пріоритет + відео для кожного --- */}
                    {priorityColors.length > 0 && (
                        <div className="form-group" style={{ marginTop: '16px' }}>
                            <label className="form-label">Кольори товару (пріоритет показу + фото та відео)</label>
                            <span className="offer-visibility-toggle__hint" style={{ marginBottom: 8 }}>
                                Перетягуйте кольори, щоб задати порядок: <b>верхній</b> відкривається на сторінці товару без фільтра; якщо його немає в наявності — наступний за пріоритетом. Для кожного кольору можна задати <b>фото та відео одразу на всі розміри</b> цього кольору (працює навіть якщо розміри на інших сторінках).
                            </span>
                            <p style={{ margin: '0 0 10px', fontSize: 12, lineHeight: 1.5, color: '#B45309', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 10px' }}>
                                💡 {VIDEO_ADVICE}
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {priorityColors.map((c, idx) => {
                                    const hex = colorPresetValueToHex(c.value) || '#cccccc';
                                    const videoUrl = colorVideoUrlOf(c.value);
                                    const uploading = colorUploading === c.value;
                                    const photos = colorPhotosOf(c.value);
                                    const photosCount = photos.length;
                                    const photoUploading = colorPhotoUploading === c.value;
                                    const rowBusy = uploading || photoUploading;
                                    const miniBtnStyle = { border: '1px solid #E4E4E7', background: '#fff', borderRadius: 7, padding: '6px 10px', fontSize: 12, cursor: 'pointer' };
                                    const sectionLabelStyle = { fontSize: 12, fontWeight: 700, color: '#64748B', flex: '0 0 52px' };
                                    return (
                                        <div
                                            key={c.value}
                                            draggable
                                            onDragStart={() => { colorDragRef.current = idx; }}
                                            onDragOver={(e) => e.preventDefault()}
                                            onDrop={() => { moveColor(colorDragRef.current, idx); colorDragRef.current = null; }}
                                            onDragEnd={() => { colorDragRef.current = null; }}
                                            style={{
                                                display: 'flex', flexDirection: 'column', gap: 10,
                                                padding: '10px 12px', border: '1px solid #E4E4E7', borderRadius: 10,
                                                background: '#fff',
                                            }}
                                        >
                                            {/* Рядок 1: колір + кількість фото */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                <span title="Перетягнути" style={{ cursor: 'grab', color: '#9CA3AF', fontSize: 16, userSelect: 'none' }}>⠿</span>
                                                <span style={{ width: 26, height: 26, borderRadius: '50%', background: hex, border: '1px solid rgba(13,13,13,0.2)', flex: '0 0 auto' }} />
                                                <span style={{ flex: '1 1 auto', minWidth: 0, fontWeight: 600 }}>
                                                    {c.label || c.value}
                                                    {idx === 0 && (
                                                        <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: '#B5179E', background: '#FBE3F4', borderRadius: 6, padding: '2px 7px' }}>
                                                            за замовчуванням
                                                        </span>
                                                    )}
                                                </span>
                                                {photosCount > 0 && (
                                                    <span style={{ display: 'inline-flex', gap: 4, flex: '0 0 auto' }}>
                                                        {photos.slice(0, 4).map((p, i) => (
                                                            <img key={i} src={getFullImageUrl(p)} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 5, border: '1px solid #E4E4E7' }} />
                                                        ))}
                                                    </span>
                                                )}
                                                <span style={{ fontSize: 12, color: photosCount ? '#16A34A' : '#9CA3AF', fontWeight: 600, flex: '0 0 auto' }}>
                                                    {photosCount ? `${photosCount} фото` : 'немає фото'}
                                                </span>
                                            </div>

                                            {/* Рядок 2: фото на всі розміри кольору */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                <span style={sectionLabelStyle}>Фото:</span>
                                                <label style={{ padding: '7px 12px', backgroundColor: '#FF4FA3', color: '#fff', borderRadius: 8, fontWeight: 600, fontSize: 12, cursor: rowBusy ? 'wait' : 'pointer', flex: '0 0 auto' }}>
                                                    {photoUploading ? 'Завантаження…' : 'Завантажити фото для кольору'}
                                                    <input
                                                        type="file"
                                                        accept="image/jpeg, image/png, image/webp, image/gif, image/svg+xml, image/avif, image/tiff, image/bmp, image/heic, image/heif, .heic, .heif"
                                                        multiple
                                                        style={{ display: 'none' }}
                                                        disabled={rowBusy}
                                                        onChange={(e) => { const fs = Array.from(e.currentTarget.files || []); e.currentTarget.value = ''; uploadPhotosForColor(c.value, fs); }}
                                                    />
                                                </label>
                                                <button
                                                    type="button"
                                                    onClick={() => applyPhotosFromSizeToColor(c.value)}
                                                    disabled={!photosCount || rowBusy}
                                                    title="Взяти фото першого розміру цього кольору і поставити такі ж усім його розмірам"
                                                    style={{ ...miniBtnStyle, opacity: (!photosCount || rowBusy) ? 0.55 : 1, cursor: (!photosCount || rowBusy) ? 'not-allowed' : 'pointer' }}
                                                >
                                                    Скопіювати фото на всі розміри
                                                </button>
                                            </div>

                                            {/* Рядок 3: відео на всі розміри кольору */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                <span style={sectionLabelStyle}>Відео:</span>
                                                {videoUrl ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            onClick={() => setVideoPreview({ url: getFullImageUrl(videoUrl), label: c.label || c.value })}
                                                            title="Переглянути відео"
                                                            style={{ position: 'relative', padding: 0, border: 0, background: 'transparent', cursor: 'pointer', flex: '0 0 auto', lineHeight: 0 }}
                                                        >
                                                            <video src={getFullImageUrl(videoUrl)} muted playsInline preload="metadata"
                                                                style={{ height: 40, borderRadius: 6, background: '#000', display: 'block', pointerEvents: 'none' }} />
                                                            <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14, textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>▶</span>
                                                        </button>
                                                        <span style={{ fontSize: 12, color: '#16A34A', fontWeight: 600 }}>є відео</span>
                                                        <button type="button"
                                                            onClick={() => setVideoPreview({ url: getFullImageUrl(videoUrl), label: c.label || c.value })}
                                                            style={miniBtnStyle}>
                                                            Переглянути
                                                        </button>
                                                        <button type="button" onClick={() => removeVideoFromColor(c.value)} title="Прибрати відео"
                                                            style={miniBtnStyle}>
                                                            Прибрати
                                                        </button>
                                                    </>
                                                ) : (
                                                    <span style={{ fontSize: 12, color: '#9CA3AF' }}>немає відео</span>
                                                )}
                                                <label style={{ padding: '7px 12px', backgroundColor: '#FF99D6', color: '#fff', borderRadius: 8, fontWeight: 600, fontSize: 12, cursor: rowBusy ? 'wait' : 'pointer', flex: '0 0 auto' }}>
                                                    {uploading ? 'Завантаження…' : (videoUrl ? 'Змінити відео' : 'Завантажити відео')}
                                                    <input
                                                        type="file"
                                                        accept="video/mp4, video/webm, video/quicktime, .mp4, .webm, .mov"
                                                        style={{ display: 'none' }}
                                                        disabled={rowBusy}
                                                        onChange={(e) => { const f = e.currentTarget.files?.[0]; e.currentTarget.value = ''; uploadVideoForColor(c.value, f); }}
                                                    />
                                                </label>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {videoPreview && createPortal(
                <div
                    onClick={() => setVideoPreview(null)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(13,13,13,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}
                >
                    <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, padding: 16, maxWidth: 'min(90vw, 720px)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                            <strong style={{ fontSize: 15 }}>Відео: {videoPreview.label}</strong>
                            <button type="button" onClick={() => setVideoPreview(null)} title="Закрити"
                                style={{ border: '1px solid #E4E4E7', background: '#fff', borderRadius: 7, width: 30, height: 30, cursor: 'pointer' }}>✕</button>
                        </div>
                        <video src={videoPreview.url} controls autoPlay playsInline
                            style={{ maxWidth: '100%', maxHeight: '78vh', borderRadius: 8, background: '#000' }} />
                    </div>
                </div>,
                document.body
            )}

            {/* === БЛОК ФІЛЬТРАЦІЇ ОФФЕРІВ ПО ОСЯМ === */}
            {axes.length > 0 && (
                <div
                    className="offers-filter-bar"
                    style={{
                        marginBottom: '20px',
                        padding: '12px 14px',
                        border: '1px solid #E4E4E7',
                        borderRadius: '12px',
                        background: '#FAFAFA',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <strong style={{ fontSize: 14, color: '#0F172A' }}>Фільтр оферів</strong>
                            {activeFilterCount > 0 && (
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: '#FF4FA3', borderRadius: 999, padding: '2px 8px' }}>
                                    {activeFilterCount}
                                </span>
                            )}
                            <span style={{ fontSize: 12, color: '#64748B' }}>
                                {activeFilterCount > 0
                                    ? `Знайдено: ${filteredOffers.length} з ${offers.length}`
                                    : `Усього оферів: ${offers.length}`}
                            </span>
                        </div>
                        {activeFilterCount > 0 && (
                            <button
                                type="button"
                                onClick={handleResetFilters}
                                style={{ border: '1px solid #E4E4E7', background: '#fff', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, color: '#475569', cursor: 'pointer' }}
                            >
                                Скинути фільтри
                            </button>
                        )}
                    </div>
                    <div className="offers-filters" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        {axes.map((axis) => {
                            const filterOptions = [
                                { value: '', label: 'Всі' },
                                ...sortAxisOptionsAsc(axis.valuesPreset?.map(val => ({
                                    value: getAxisPresetValue(val),
                                    label: getAxisPresetLabel(val)
                                })) || [])
                            ];

                            return (
                                <div key={`filter-${axis.axisId}`} style={{ flex: '1 1 150px', minWidth: '140px', maxWidth: '220px' }}>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#64748B', marginBottom: 3 }}>
                                        {axis.title?.ua || axis.axisId}
                                    </label>
                                    <CustomSelect
                                        options={filterOptions}
                                        value={currentOpt[axis.axisId] || ''}
                                        onChange={(val) => handleFilterChange(axis.axisId, val)}
                                        placeholder="Всі"
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {offerPageCount > 1 && (
                <CatalogPagination page={offerPage} pageCount={offerPageCount} onPageChange={setOfferPage} />
            )}

            <div className="product__variations">
                {pagedOffers.map(({ item, originalIndex }) => (
                    <div className="product__variation" key={originalIndex}>

                        <div className="product__variation__header">
                            <div className="product__variation__title-wrapper">
                                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 11 11" fill="none">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M1.02745 0.0858936C1.37383 0.0388511 1.87187 0 2.57447 0C3.27706 0 3.77511 0.0388511 4.12149 0.0858936C4.70426 0.165468 5.07755 0.62934 5.11266 1.18168C5.13279 1.49694 5.14894 1.94536 5.14894 2.57447C5.14894 3.20357 5.13279 3.652 5.11266 3.96726C5.07755 4.5196 4.70426 4.98347 4.12126 5.06304C3.77511 5.11009 3.2773 5.14894 2.57447 5.14894C1.87164 5.14894 1.37383 5.11009 1.02745 5.06304C0.444681 4.98347 0.071617 4.51936 0.0365106 3.96702C0.0161489 3.652 0 3.20357 0 2.57447C0 1.94536 0.0161489 1.49694 0.0362766 1.18168C0.071383 0.62934 0.444681 0.165468 1.02745 0.0858936ZM1.02745 10.9141C1.37383 10.9611 1.87164 11 2.57447 11C3.2773 11 3.77511 10.9611 4.12149 10.9141C4.70426 10.8345 5.07755 10.3707 5.11266 9.81832C5.13279 9.50306 5.14894 9.05464 5.14894 8.42553C5.14894 7.79643 5.13279 7.348 5.11266 7.03274C5.07755 6.4804 4.70426 6.01653 4.12126 5.93696C3.77511 5.88991 3.2773 5.85106 2.57447 5.85106C1.87164 5.85106 1.37383 5.88991 1.02745 5.93696C0.444681 6.01653 0.071617 6.48064 0.0365106 7.03298C0.0161489 7.348 0 7.79643 0 8.42553C0 9.05464 0.0161489 9.50306 0.0362766 9.81832C0.071383 10.3707 0.444681 10.8345 1.02745 10.9141ZM11 8.42553C11 9.12836 10.9611 9.62617 10.9141 9.97255C10.8345 10.5553 10.3707 10.9286 9.81832 10.9637C9.50306 10.9839 9.05464 11 8.42553 11C7.79643 11 7.348 10.9839 7.03274 10.9637C6.4804 10.9286 6.01653 10.5551 5.93696 9.97232C5.88991 9.62617 5.85106 9.12836 5.85106 8.42553C5.85106 7.7227 5.88991 7.22489 5.93696 6.87851C6.01653 6.29574 6.4804 5.92245 7.03274 5.88734C7.348 5.86721 7.79643 5.85106 8.42553 5.85106C9.05464 5.85106 9.50306 5.86721 9.81832 5.88734C10.3707 5.92245 10.8345 6.29574 10.9141 6.87875C10.9611 7.22489 11 7.7227 11 8.42553ZM7.91228 0.323915C8.13415 -0.0816809 8.71692 -0.0816809 8.93879 0.323915L9.405 1.1763C9.50165 1.35303 9.64697 1.49835 9.8237 1.595L10.6761 2.06121C11.0817 2.28309 11.0817 2.86585 10.6761 3.08772L9.8237 3.55394C9.64697 3.65059 9.50165 3.7959 9.405 3.97264L8.93879 4.82502C8.71692 5.23062 8.13415 5.23062 7.91228 4.82502L7.44606 3.97264C7.34941 3.7959 7.2041 3.65059 7.02736 3.55394L6.17498 3.08772C5.76938 2.86585 5.76938 2.28309 6.17498 2.06121L7.02736 1.595C7.2041 1.49835 7.34941 1.35303 7.44606 1.1763L7.91228 0.323915Z" fill="#0F172A" />
                                </svg>
                                <h3>{item.title || `Нова варіація ${originalIndex + 1}`}</h3>
                            </div>
                            <button
                                type="button"
                                onClick={() => handleRemoveOffer(originalIndex)}
                                className="btn-remove"
                            >
                                - Видалити оффер
                            </button>
                        </div>

                        <div className="product__variation__content">
                            <div className="variation-details">
                                <div className="form-wrapper-3-column">
                                    <CustomInput
                                        id={`offers-${originalIndex}-price`} name={`offers[${originalIndex}].price`}
                                        label="Ціна" type="number"
                                        value={formik.values.offers?.[originalIndex]?.price || ''} onChange={formik.handleChange}
                                        placeholder="0.00"
                                    />
                                    <CustomInput
                                        id={`offers-${originalIndex}-sku`} name={`offers[${originalIndex}].sku`}
                                        label="Артикул (SKU)"
                                        value={formik.values.offers?.[originalIndex]?.sku || ''} onChange={(e) => handleSkuChange(originalIndex, e)}
                                        onBlur={() => handleSkuBlur(originalIndex)}
                                        placeholder="Введіть або згенерується автоматично"
                                    />
                                    <div className="form-group">
                                        <label htmlFor={`offers-${originalIndex}-accessory`}>Товар для допродажу</label>
                                        <CustomSelect
                                            options={accessoryOptions}
                                            value={String(formik.values.offers?.[originalIndex]?.accessoryGroupId || '')}
                                            onChange={(val) => formik.setFieldValue(`offers[${originalIndex}].accessoryGroupId`, val)}
                                            onInputChange={setAccessorySearchQuery}
                                            name={`offers[${originalIndex}].accessoryGroupId`}
                                            id={`offers-${originalIndex}-accessory`}
                                            placeholder={isAccessorySearchLoading ? 'Пошук товарів...' : 'Оберіть товар'}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="offer-visibility-toggle" htmlFor={`offers-${originalIndex}-available`}>
                                            <input
                                                id={`offers-${originalIndex}-available`}
                                                name={`offers[${originalIndex}].available`}
                                                type="checkbox"
                                                checked={formik.values.offers?.[originalIndex]?.available !== false}
                                                onChange={(e) => formik.setFieldValue(`offers[${originalIndex}].available`, e.target.checked)}
                                            />
                                            <span>Показувати на сайті</span>
                                        </label>
                                        <span className="offer-visibility-toggle__hint">
                                            Вимкніть, щоб приховати цей офер на сайті.
                                        </span>
                                    </div>
                                </div>

                                <div className="form-wrapper-2-column">
                                    <div className="form-group">
                                        <label htmlFor={`offers-${originalIndex}-discountType`}>Тип знижки</label>
                                        <CustomSelect
                                            options={DISCOUNT_TYPE_OPTIONS}
                                            value={formik.values.offers?.[originalIndex]?.discountType || 'none'}
                                            onChange={(val) => setOfferDiscountType(originalIndex, val)}
                                            name={`offers[${originalIndex}].discountType`}
                                            id={`offers-${originalIndex}-discountType`}
                                            placeholder="Без знижки"
                                        />
                                    </div>
                                    {(formik.values.offers?.[originalIndex]?.discountType === 'percent') && (
                                        <CustomInput
                                            id={`offers-${originalIndex}-discount`} name={`offers[${originalIndex}].discount`}
                                            label="Знижка (%)" type="number"
                                            value={formik.values.offers?.[originalIndex]?.discount || ''} onChange={formik.handleChange}
                                            placeholder="0"
                                        />
                                    )}
                                    {(formik.values.offers?.[originalIndex]?.discountType === 'amount') && (
                                        <CustomInput
                                            id={`offers-${originalIndex}-discountUAH`} name={`offers[${originalIndex}].discountUAH`}
                                            label="Знижка (грн)" type="number"
                                            value={formik.values.offers?.[originalIndex]?.discountUAH || ''} onChange={formik.handleChange}
                                            placeholder="0"
                                        />
                                    )}
                                </div>

                                <div className="form-wrapper-2-column">
                                    {axes.map((axis) => {
                                        const selectOptions = sortAxisOptionsAsc(
                                            axis.valuesPreset?.map(val => ({
                                                value: getAxisPresetValue(val),
                                                label: getAxisPresetLabel(val)
                                            })) || []
                                        );

                                        return (
                                            <div className="form-group" key={axis.axisId}>
                                                <label>{axis.title?.ua || axis.axisId}</label>
                                                <CustomSelect
                                                    options={selectOptions}
                                                    value={String(formik.values.offers?.[originalIndex]?.options?.[axis.axisId] || '')}
                                                    onChange={(val) => formik.setFieldValue(`offers[${originalIndex}].options.${axis.axisId}`, val)}
                                                    placeholder="Оберіть опцію"
                                                    isClearable
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="variation-image-upload">
                                <label className="variation-image-upload__label">
                                    Зображення ({getOfferImages(formik.values.offers?.[originalIndex] || item).length}/{MAX_OFFER_IMAGES})
                                </label>

                                <div className="variation-image-upload__slots">
                                    {getOfferImageSlots(formik.values.offers?.[originalIndex] || item).map((imgUrl, imageIndex) => (
                                        <div
                                            className="variation-image-upload__slot"
                                            key={`offer-${originalIndex}-image-slot-${imageIndex}`}
                                            draggable={Boolean(imgUrl)}
                                            onDragStart={() => {
                                                if (imgUrl) dragMediaRef.current = { offerIndex: originalIndex, imageIndex };
                                            }}
                                            onDragOver={(e) => {
                                                const d = dragMediaRef.current;
                                                if (d && d.offerIndex === originalIndex) e.preventDefault();
                                            }}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                const d = dragMediaRef.current;
                                                if (d && d.offerIndex === originalIndex) {
                                                    moveOfferImage(originalIndex, d.imageIndex, imageIndex);
                                                }
                                                dragMediaRef.current = null;
                                            }}
                                            onDragEnd={() => { dragMediaRef.current = null; }}
                                            style={imgUrl ? { cursor: 'grab' } : undefined}
                                            title={imgUrl ? 'Перетягніть, щоб змінити порядок фото' : undefined}
                                        >
                                            <div
                                                className="variation-image-upload__box"
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    position: 'relative',
                                                    padding: '15px',
                                                    textAlign: 'center',
                                                    border: '1px dashed #ccc',
                                                    borderRadius: '8px'
                                                }}
                                            >
                                                {imgUrl ? (
                                                    <>
                                                        {isVideoMediaUrl(imgUrl) ? (
                                                            <video
                                                                src={getFullImageUrl(imgUrl)}
                                                                muted
                                                                playsInline
                                                                preload="metadata"
                                                                style={{
                                                                    width: '100%',
                                                                    maxHeight: '120px',
                                                                    objectFit: 'contain',
                                                                    borderRadius: '4px',
                                                                    marginBottom: '8px',
                                                                    background: '#000'
                                                                }}
                                                            />
                                                        ) : (
                                                            <img
                                                                src={getFullImageUrl(imgUrl)}
                                                                alt={`Offer media ${imageIndex + 1}`}
                                                                style={{
                                                                    width: '100%',
                                                                    maxHeight: '120px',
                                                                    objectFit: 'contain',
                                                                    borderRadius: '4px',
                                                                    marginBottom: '8px'
                                                                }}
                                                            />
                                                        )}
                                                        <span className="variation-image-upload__text" style={{ color: '#FF99D6', fontWeight: '500' }}>
                                                            {isVideoMediaUrl(imgUrl) ? 'Змінити медіа' : 'Змінити фото'} #{imageIndex + 1}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44" fill="none" style={{ marginBottom: '8px' }}>
                                                            <path d="M39.1111 4.88889V39.1111H4.88889V4.88889H39.1111ZM39.1111 0H4.88889C2.2 0 0 2.2 0 4.88889V39.1111C0 41.8 2.2 44 4.88889 44H39.1111C41.8 44 44 41.8 44 39.1111V4.88889C44 2.2 41.8 0 39.1111 0ZM27.2311 21.6578L19.8978 31.1178L14.6667 24.7867L7.33333 34.2222H36.6667L27.2311 21.6578Z" fill="#FF99D6" />
                                                        </svg>
                                                        <span className="variation-image-upload__text" style={{ color: '#FF99D6', fontWeight: '500' }}>
                                                            Додати медіа #{imageIndex + 1}
                                                        </span>
                                                    </>
                                                )}

                                                <input
                                                    type="file"
                                                    accept="image/jpeg, image/png, image/webp, image/gif, image/svg+xml, image/avif, image/tiff, image/bmp, image/heic, image/heif, .heic, .heif, video/mp4, video/webm, video/quicktime, .mp4, .webm, .mov"
                                                    style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        opacity: 0,
                                                        cursor: 'pointer'
                                                    }}
                                                    onChange={async (e) => {
                                                        const inputEl = e.currentTarget;
                                                        const file = inputEl?.files?.[0];
                                                        if (!file) return;
                                                        if (isVideoFile(file) && !isVideoFileWithinLimit(file)) { if (inputEl) inputEl.value = ''; return; }

                                                        try {
                                                            // Видео и фото — в один медиа-список цвета. Видео грузим
                                                            // как есть (webm-транскод идёт фоном на сервере).
                                                            const response = isVideoFile(file)
                                                                ? await uploadAdminVideo(file, { transcode: false })
                                                                : await uploadAdminImages(file);
                                                            const uploadedUrl = getFullImageUrl(
                                                                response?.file?.url || response?.url || response?.data?.[0]?.url
                                                            );
                                                            if (uploadedUrl) {
                                                                setOfferImageByIndex(originalIndex, imageIndex, uploadedUrl);
                                                            } else {
                                                                toast.error('Не вдалося отримати URL завантаженого медіа');
                                                            }
                                                        } catch (error) {
                                                            console.error('Upload error:', error);
                                                            toast.error('Помилка при завантаженні медіа');
                                                        } finally {
                                                            if (inputEl) {
                                                                inputEl.value = '';
                                                            }
                                                        }
                                                    }}
                                                    className="variation-image-upload__input"
                                                />
                                            </div>

                                            <input
                                                type="text"
                                                placeholder="Або вставте URL..."
                                                value={imgUrl}
                                                onChange={(e) => setOfferImageByIndex(originalIndex, imageIndex, e.target.value)}
                                                style={{
                                                    marginTop: '8px',
                                                    width: '100%',
                                                    padding: '6px 10px',
                                                    fontSize: '12px',
                                                    border: '1px solid #CBD5E1',
                                                    borderRadius: '6px',
                                                    color: '#475569',
                                                    outline: 'none',
                                                    boxSizing: 'border-box'
                                                }}
                                            />

                                            {imgUrl && (
                                                <button
                                                    type="button"
                                                    className="variation-image-upload__remove-btn"
                                                    onClick={() => removeOfferImage(originalIndex, imageIndex)}
                                                >
                                                    Видалити фото #{imageIndex + 1}
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                <button type="button" onClick={handleAddOffer} className="btn-add-primary">
                    + Додати варіацію
                </button>
            </div>

            {/* === ПАГІНАЦІЯ ОФФЕРІВ (клієнтська) === */}
            {offerPageCount > 1 && (
                <CatalogPagination page={offerPage} pageCount={offerPageCount} onPageChange={setOfferPage} />
            )}

            {/* === ДЕБАГ: ПОМИЛКИ ФОРМИ === */}
            {/* {Object.keys(formik.errors).length > 0 && (
                <div style={{ marginTop: '24px', padding: '16px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px' }}>
                    <strong style={{ color: '#DC2626', fontSize: '13px' }}>Помилки форми (чому не зберігається):</strong>
                    <pre style={{ marginTop: '8px', fontSize: '12px', color: '#7F1D1D', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {JSON.stringify(formik.errors, null, 2)}
                    </pre>
                </div>
            )} */}

        </form>
        <BulkOffersModal
            isOpen={isBulkOffersModalOpen}
            onClose={onCloseBulkOffersModal}
            selectableAxes={selectableAxes}
            offers={offers}
            setFieldValue={formik.setFieldValue}
        />
        </>
    );
};

export default ProductForm;
